import React, { useCallback, useEffect, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import { ApplicationEndpoints } from "@generated/application";
import { apiFetch } from "@platform/http/apiClient";
import { Application } from "src/domains/application/types";

// ── Types ────────────────────────────────────────────────────────────────────

interface ManifestPreviewTabProps {
  system: Application;
}

interface ManifestPreviewResponse {
  yaml: string;
  services?: { name: string; type: string }[];
}

// ── Component ────────────────────────────────────────────────────────────────

export function ManifestPreviewTab({
  system,
}: ManifestPreviewTabProps): React.ReactElement {
  const [yaml, setYaml] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchManifest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<ManifestPreviewResponse>(
        ApplicationEndpoints.GET_APPLICATION_MANIFEST.replace(
          ":id",
          system.alias,
        ),
      );
      setYaml(res.yaml ?? JSON.stringify(res, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load manifest");
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchManifest();
  }, [fetchManifest]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(yaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ── Card wrapper matching mockup ───────────────────────────────────────────
  const cardSx = {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };

  return (
    <Box sx={cardSx}>
      {/* Card header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          rusha.yml
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={() => void fetchManifest()}
            disabled={loading}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopy}
            disabled={loading || !yaml}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 600 }}
          >
            {copied ? "Copied ✓" : "Copy"}
          </Button>
        </Stack>
      </Stack>

      {/* Content */}
      {loading ? (
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2.5,
            bgcolor: "rgba(10,20,36,0.4)",
            overflow: "auto",
            fontFamily: "monospace",
            fontSize: 12,
            lineHeight: 1.6,
            color: "text.secondary",
            borderBottom: "1px solid",
            borderColor: "divider",
            borderRadius: 0,
          }}
        >
          {yaml || "No manifest available."}
        </Box>
      )}
    </Box>
  );
}

export default ManifestPreviewTab;
